/*
  Kit Configurator (builder-only)
  Reads its initial data from a <script type="application/json"
  data-kit-configurator-state> island that the Liquid section emits.
  All variant IDs and prices are server-rendered, so the JS never has
  to hit the Shopify API to look anything up.

  Add-to-cart submits one POST to /cart/add.js with the full kit's
  variants in a single call (Shopify's documented batch endpoint).
*/

(function () {
  'use strict';

  function boot() {
    var roots = document.querySelectorAll('[data-kit-configurator]');
    for (var i = 0; i < roots.length; i++) initOne(roots[i]);
  }

  function initOne(root) {
    if (root.__kcBooted) return;
    root.__kcBooted = true;

    var dataEl = root.querySelector('[data-kit-configurator-state]');
    if (!dataEl) return;
    var raw;
    try {
      raw = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Kit Configurator: failed to parse state', e);
      return;
    }

    if (!Array.isArray(raw.fans) || raw.fans.length === 0) {
      root.innerHTML = '<p style="padding:24px;color:var(--kc-ink-soft,#5b626d);">Kit Configurator is not configured for this product yet.</p>';
      return;
    }
    if (!Array.isArray(raw.apps)) raw.apps = [];
    if (!Array.isArray(raw.addons)) raw.addons = [];
    if (!raw.addonsByFan || typeof raw.addonsByFan !== 'object') raw.addonsByFan = {};

    new KitConfigurator(root, raw);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  class KitConfigurator {
    constructor(root, data) {
      this.root = root;
      this.data = data;

      const firstFanAddons = data.addonsByFan[data.fans[0].id] || data.addons;
      const preselectedAddons = {};
      firstFanAddons.forEach(a => { preselectedAddons[a.key] = !!a.preselected; });
      const defaultApp = (data.apps.find(a => a.default) || data.apps[0] || {}).key || 'none';

      this.state = {
        fanId: data.fans[0].id,
        app: defaultApp,
        addonSel: preselectedAddons,
        added: false,
        submitting: false,
      };

      this.bindEvents();
      this.render();
    }

    bindEvents() {
      const cta = this.root.querySelector('[data-add-to-cart]');
      if (cta) cta.addEventListener('click', () => this.addToCart());
    }

    setFan(id) {
      if (this.state.submitting) return;
      this.state.fanId = id;
      // Seed selection defaults for any addon key this fan has that the
      // user hasn't touched yet; existing choices carry across fan switches.
      this.currentAddons().forEach(a => {
        if (!(a.key in this.state.addonSel)) this.state.addonSel[a.key] = !!a.preselected;
      });
      this.state.added = false;
      this.render();
    }
    setApp(key) {
      if (this.state.submitting) return;
      this.state.app = (this.state.app === key) ? 'none' : key;
      this.state.added = false;
      this.render();
    }
    toggleAddon(key) {
      if (this.state.submitting) return;
      this.state.addonSel[key] = !this.state.addonSel[key];
      this.state.added = false;
      this.render();
    }

    selectedFan() {
      return this.data.fans.find(f => f.id === this.state.fanId) || this.data.fans[0];
    }
    currentAddons() {
      return this.data.addonsByFan[this.state.fanId] || this.data.addons;
    }
    selectedApp() {
      if (this.state.app === 'none') return null;
      return this.data.apps.find(a => a.key === this.state.app) || null;
    }
    lines() {
      const fan = this.selectedFan();
      const out = [];
      let total = fan.price;
      out.push({
        key: 'fan',
        name: fan.name + ' Radon Fan',
        meta: 'Inlet ' + (fan.size || ''),
        price: fan.price,
      });
      if (fan.couplingVariantId) {
        const couplingTotal = (fan.couplingPrice || 0) * (fan.couplingQuantity || 2);
        if (couplingTotal > 0) {
          total += couplingTotal;
          out.push({
            key: 'couplings',
            name: 'Spruce Coupling × ' + (fan.couplingQuantity || 2),
            meta: (fan.coupling || '') + ' fittings',
            price: couplingTotal,
          });
        }
      }
      this.currentAddons().forEach(a => {
        if (this.state.addonSel[a.key]) {
          total += a.price;
          out.push({ key: a.key, name: a.label, meta: a.subtitle || '', price: a.price });
        }
      });
      const app = this.selectedApp();
      if (app) {
        total += app.price;
        out.push({ key: app.key, name: app.label, meta: 'Application', price: app.price });
        // Any "extras" bundled with this application — auto-included accessories.
        (app.extras || []).forEach((x, i) => {
          const lineTotal = (x.price || 0) * (x.quantity || 1);
          total += lineTotal;
          out.push({
            key: app.key + '__extra' + i,
            name: (x.quantity > 1 ? x.label + ' × ' + x.quantity : x.label),
            meta: 'Included with ' + app.label,
            price: lineTotal,
          });
        });
      }
      return { lines: out, total };
    }

    money(n) { return '$' + (Math.round(n * 100) / 100).toFixed(2); }

    async addToCart() {
      if (this.state.submitting) return;
      this.state.submitting = true;
      this.renderCtaState();

      const fan = this.selectedFan();
      // Unique identifier so all items in this submission can be grouped together
      // in the order. Lets us see "this customer built this kit on date X".
      const kitId = 'KIT-' + Math.random().toString(36).slice(2, 8).toUpperCase() +
                    '-' + (fan.id || 'fan').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
      // Properties prefixed with `_` are hidden from the customer-facing cart/checkout
      // but show up on the Order detail page in Shopify Admin.
      const prop = (role) => ({ '_Kit': kitId, '_Kit Role': role });

      const items = [];
      items.push({ id: fan.variantId, quantity: 1, properties: prop('Fan') });
      if (fan.couplingVariantId) {
        items.push({ id: fan.couplingVariantId, quantity: fan.couplingQuantity || 2, properties: prop('Coupling') });
      }
      const app = this.selectedApp();
      if (app) {
        items.push({ id: app.variantId, quantity: 1, properties: prop('Application: ' + app.label) });
        (app.extras || []).forEach(x => {
          if (x.variantId) items.push({ id: x.variantId, quantity: x.quantity || 1, properties: prop('Application extra (' + app.label + ')') });
        });
      }
      this.currentAddons().forEach(a => {
        if (this.state.addonSel[a.key]) items.push({ id: a.variantId, quantity: 1, properties: prop('Add-on: ' + a.label) });
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || res.statusText);
        }
        // Tag the cart so the Order page in Admin shows "Kit Builder = yes".
        // Best-effort: errors here don't block the success flow.
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              attributes: {
                'Kit Builder': 'Yes',
                'Kit ID': kitId,
                'Kit Fan': fan.name || '',
                'Kit Application': app ? app.label : 'None',
              },
            }),
          });
        } catch (e) { /* non-blocking */ }

        this.state.added = true;
        this.state.submitting = false;
        this.clearError();
        this.render();
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
          drawer.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
        }
        // Generic events for any other theme listening at the document level.
        ['cart:refresh', 'cart:added', 'cart:updated'].forEach(name => {
          document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
        });
      } catch (err) {
        clearTimeout(timeoutId);
        console.error('Kit Configurator add-to-cart failed', err);
        this.state.submitting = false;
        const msg = err.name === 'AbortError'
          ? 'Your connection timed out. Please try again.'
          : (err.message || 'Something went wrong.');
        this.showError('Sorry — we couldn’t add the kit to your cart. ' + msg);
        this.renderCtaState();
      }
    }

    renderCtaState() {
      const cta = this.root.querySelector('[data-add-to-cart]');
      if (!cta) return;
      cta.disabled = this.state.submitting;
      cta.textContent = this.state.submitting ? 'Adding…' : 'Add kit to cart';
    }

    showError(msg) {
      const el = this.root.querySelector('[data-error]');
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
    }
    clearError() {
      const el = this.root.querySelector('[data-error]');
      if (!el) return;
      el.textContent = '';
      el.hidden = true;
    }

    render() {
      this.renderFanGrid();
      this.renderAppGrid();
      this.renderAddons();
      this.renderSummary();
      this.renderCtaState();
    }

    renderFanGrid() {
      const host = this.root.querySelector('[data-fan-grid]');
      if (!host) return;
      host.innerHTML = this.data.fans.map(f => {
        const isSel = this.state.fanId === f.id;
        const imgHTML = f.image && /^(https?:|\/)/i.test(String(f.image))
          ? `<img src="${escapeAttr(f.image)}" alt="${escapeAttr(f.name || '')}">`
          : `fan photo`;
        const specs = f.specs || {};
        const specRows = [
          specs.wattage      && { label: 'Wattage',      value: specs.wattage },
          specs.cfm          && { label: 'Airflow',      value: specs.cfm },
          specs.max_pressure && { label: 'Max pressure', value: specs.max_pressure },
          specs.hub          && { label: 'Hub size',     value: specs.hub },
        ].filter(Boolean);
        const specsHTML = specRows.length
          ? `<dl class="kit-configurator__fan-specs">${specRows.map(s => `
              <div class="kit-configurator__fan-spec">
                <dt>${escapeHTML(s.label)}</dt>
                <dd>${escapeHTML(s.value)}</dd>
              </div>`).join('')}</dl>`
          : '';
        return `
          <button type="button" class="kit-configurator__fan-card ${isSel ? 'is-selected' : ''}" data-fan-pick="${escapeAttr(f.id)}" aria-pressed="${isSel}">
            <div class="kit-configurator__fan-photo">${imgHTML}</div>
            <div class="kit-configurator__fan-head">
              <span class="kit-configurator__fan-name">${escapeHTML(f.name || '')}</span>
              <span class="kit-configurator__fan-size">${escapeHTML(f.size || '')} inlet</span>
            </div>
            ${f.tag ? `<div class="kit-configurator__fan-tag">${escapeHTML(f.tag)}</div>` : ''}
            ${specsHTML}
            <div class="kit-configurator__fan-foot">
              <span class="kit-configurator__fan-price">${this.money(f.price)}</span>
              <span class="kit-configurator__fan-coupling">${escapeHTML(f.coupling || '')} coupling</span>
            </div>
          </button>`;
      }).join('');
      host.querySelectorAll('[data-fan-pick]').forEach(b => {
        b.addEventListener('click', () => this.setFan(b.dataset.fanPick));
      });
    }

    renderAppGrid() {
      const host = this.root.querySelector('[data-app-grid]');
      if (!host) return;
      const cards = this.data.apps.map(a => {
        const isSel = this.state.app === a.key;
        const extras = (a.extras || []).filter(x => x.variantId);
        const extrasTotal = extras.reduce((sum, x) => sum + (x.price || 0) * (x.quantity || 1), 0);
        const bundleTotal = a.price + extrasTotal;
        const extrasHTML = extras.length
          ? `<div class="kit-configurator__app-extras">+ Includes ${extras.map(x => escapeHTML(x.label)).join(', ')}</div>`
          : '';
        return `
          <button type="button" class="kit-configurator__app-card ${isSel ? 'is-selected' : ''}" data-app-pick="${escapeAttr(a.key)}" role="radio" aria-checked="${isSel}">
            <div class="kit-configurator__app-head">
              <div class="kit-configurator__app-text">
                <div class="kit-configurator__app-name">${escapeHTML(a.label || '')}</div>
                <div class="kit-configurator__app-sub">${escapeHTML(a.subtitle || '')}</div>
                ${extrasHTML}
              </div>
              <span class="kit-configurator__check ${isSel ? 'is-on' : ''}"></span>
            </div>
            <div class="kit-configurator__app-price">${this.money(bundleTotal)}</div>
          </button>`;
      }).join('');
      const noneSel = this.state.app === 'none';
      const noneCard = `
        <button type="button" class="kit-configurator__app-card ${noneSel ? 'is-selected' : ''}" data-app-pick="none" role="radio" aria-checked="${noneSel}">
          <div class="kit-configurator__app-head">
            <div class="kit-configurator__app-text">
              <div class="kit-configurator__app-name">Neither yet</div>
              <div class="kit-configurator__app-sub">Skip — I’ll add the base part later.</div>
            </div>
            <span class="kit-configurator__check ${noneSel ? 'is-on' : ''}"></span>
          </div>
        </button>`;
      host.innerHTML = cards + noneCard;
      host.querySelectorAll('[data-app-pick]').forEach(b => {
        b.addEventListener('click', () => this.setApp(b.dataset.appPick));
      });
    }

    renderAddons() {
      const host = this.root.querySelector('[data-addon-list]');
      if (!host) return;
      const fan = this.selectedFan();
      host.innerHTML = this.currentAddons().map(a => {
        const isSel = !!this.state.addonSel[a.key];
        return `
          <button type="button" class="kit-configurator__addon ${isSel ? 'is-selected' : ''}" data-addon-pick="${escapeAttr(a.key)}" aria-pressed="${isSel}">
            <span class="kit-configurator__check ${isSel ? 'is-on' : ''}"></span>
            <div class="kit-configurator__addon-text">
              <div class="kit-configurator__addon-name">${escapeHTML(a.label || '')}</div>
              <div class="kit-configurator__addon-meta">${escapeHTML(a.subtitle || '')}</div>
            </div>
            <div class="kit-configurator__addon-price">${this.money(a.price)}</div>
          </button>`;
      }).join('');
      host.querySelectorAll('[data-addon-pick]').forEach(b => {
        b.addEventListener('click', () => this.toggleAddon(b.dataset.addonPick));
      });
      this.root.querySelectorAll('[data-fan-name]').forEach(s => { s.textContent = fan.name || ''; });
      this.root.querySelectorAll('[data-coupling-size-inline]').forEach(s => { s.textContent = fan.coupling || ''; });
    }

    renderSummary() {
      const { lines, total } = this.lines();
      const linesHTML = lines.map(l => `
        <div class="kit-configurator__line">
          <div>
            <div class="kit-configurator__line-name">${escapeHTML(l.name)}</div>
            ${l.meta ? `<div class="kit-configurator__line-meta">${escapeHTML(l.meta)}</div>` : ''}
          </div>
          <div class="kit-configurator__line-price">${this.money(l.price)}</div>
        </div>`).join('');
      const linesHost = this.root.querySelector('[data-lines]');
      if (linesHost) linesHost.innerHTML = linesHTML;
      const count = lines.length;
      const countLabel = count + (count === 1 ? ' item' : ' items');
      const totalLabel = this.money(total);
      const countEl = this.root.querySelector('[data-count]');
      const totalEl = this.root.querySelector('[data-total]');
      if (countEl) countEl.textContent = countLabel;
      if (totalEl) totalEl.textContent = totalLabel;
      const added = this.root.querySelector('[data-added]');
      if (added) added.hidden = !this.state.added;
    }
  }

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }
  function escapeAttr(s) { return escapeHTML(s); }
})();
